import React, { useEffect, useMemo, useState } from "react";
import { Container, Text, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup panel settings
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;
const PANEL_BORDER_RADIUS = 20;

// Info popup configuration
const INFO_POPUP_CONFIG = {
  // Padding around content inside panel (will be multiplied by scale)
  PANEL_PADDING: 70,
  // Additional width padding (adds extra space to width only)
  PANEL_WIDTH_PADDING: 100,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 28,
        panelPaddingMultiplier: 1.0,
        widthPaddingMultiplier: 1.5,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        messageFontSize: 42,
        panelPaddingMultiplier: 1.5,
        widthPaddingMultiplier: 2.0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 42,
        panelPaddingMultiplier: 1.0,
        widthPaddingMultiplier: 1.5,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        messageFontSize: 42,
        panelPaddingMultiplier: 1.0,
        widthPaddingMultiplier: 1.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        messageFontSize: 42,
        panelPaddingMultiplier: 1.0,
        widthPaddingMultiplier: 1.5,
      },
    },
  },
};

const InfoPopup = () => {
  const { t } = useTranslation();
  const { dimensions, uiSymbolScale, device, orientation } = useDimensions();

  // TODO: Replace with actual selectors when Redux state is set up
  const isOpen = useSelector((state) => state.ui?.infoPopupActive || false);
  const messageKey = useSelector((state) => state.ui?.infoPopupKey || "");

  const [overlayAlpha, setOverlayAlpha] = useState(0);

  // Get device config
  const deviceConfig = INFO_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
                       INFO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];

  // Get message from translation
  const message = messageKey ? t(messageKey) : "";

  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  // Apply scaling
  const scale = uiSymbolScale;

  // Text styling - MUST be before early return
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: Math.round(deviceConfig.messageFontSize * scale),
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: dimensions.width * 0.7,
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [deviceConfig.messageFontSize, scale, dimensions.width]);

  // Calculate actual text height using TextMetrics - MUST be before early return
  const textMetrics = useMemo(() => {
    return TextMetrics.measureText(message, messageStyle);
  }, [message, messageStyle]);

  // Calculate dynamic positions
  const textHeight = textMetrics.height;
  const textWidth = textMetrics.width;
  const padding = INFO_POPUP_CONFIG.PANEL_PADDING * scale * deviceConfig.panelPaddingMultiplier;
  const widthPadding = INFO_POPUP_CONFIG.PANEL_WIDTH_PADDING * scale * deviceConfig.widthPaddingMultiplier;

  // Calculate panel dimensions
  // Width: text width + extra width padding for larger appearance
  // Height: text height + normal padding (respects padding)
  const panelWidth = textWidth + widthPadding * 2;
  const panelHeight = textHeight + padding * 2;

  // Center the panel perfectly in the middle of the screen
  const panelX = centerX - panelWidth / 2;
  const panelY = centerY - panelHeight / 2;

  // Position content centered within the panel
  const messageY = centerY;

  // Handle overlay fade in/out
  useEffect(() => {
    if (isOpen) {
      // Fade in
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });
    } else {
      // Fade out
      anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeInQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
        },
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    // TODO: Replace with actual close action when Redux state is set up
    // const dispatch = useDispatch();
    // dispatch(closeInfoPopup());
  };

  // Early return AFTER all hooks
  if (!isOpen) {
    return null;
  }

  return (
    <Container name="info_popup" zIndex={10000} sortableChildren={true}>
      {/* Black overlay - clickable to close */}
      <Graphics
        draw={(g) => {
          g.clear();
          g.beginFill(OVERLAY_BACKGROUND_COLOR);
          g.drawRect(0, 0, dimensions.width, dimensions.height);
          g.endFill();
        }}
        alpha={overlayAlpha}
        eventMode="static"
        cursor="pointer"
        pointerdown={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        zIndex={10000}
      />

      {/* Content container */}
      <Container name="popup_content" zIndex={10001}>
        {/* Dark panel background - wraps text only */}
        <Graphics
          draw={(g) => {
            g.clear();
            g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
            g.drawRoundedRect(
              panelX,
              panelY,
              panelWidth,
              panelHeight,
              PANEL_BORDER_RADIUS * scale
            );
            g.endFill();
          }}
        />

        {/* Message text - centered in panel */}
        <Text
          text={message}
          style={messageStyle}
          anchor={0.5}
          x={centerX}
          y={messageY}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default InfoPopup;
